import { useLocation, useNavigate } from 'react-router-dom'
import { Lock } from 'lucide-react'
import { useEnterPhaseTwo } from './GatedValue'

interface VerificationRequiredScreenProps {
  /** What the user was trying to open, e.g. "This project's data room". */
  what?: string
}

/**
 * The whole-route gate for screens that are financial end to end.
 *
 * Why a route gate rather than a blur: the project data room, the order detail
 * page, the trading screens and the portfolio are fed by endpoints that have no
 * non-financial counterpart (`GET /projects/{id}/graph`,
 * `GET /orderbook/{id}`, `GET /portfolios/...`). There is no version of those
 * pages that can be rendered from data a `browse` user is allowed to hold, and
 * a page assembled from a hundred hidden values is a hundred chances to miss
 * one — a chart tooltip enumerating every order's FDV, for instance
 * (`ProjectChart.tsx`), or a CSV export that bypasses the render layer
 * entirely.
 *
 * So the browse tier does not fetch these screens at all. The blur treatment is
 * reserved for the dashboard, where a genuine identity-only data source exists.
 */
export const VerificationRequiredScreen = ({ what }: VerificationRequiredScreenProps) => {
  const navigate = useNavigate()
  const location = useLocation()
  const enterPhaseTwo = useEnterPhaseTwo()

  return (
    <div className="flex min-h-[60vh] w-full items-center justify-center px-4 py-12">
      <div
        className={
          'flex w-full max-w-[520px] flex-col items-center gap-4 border border-[rgba(45,41,35,0.12)] ' +
          'bg-[rgba(255,255,255,0.62)] px-6 py-10 text-center ' +
          'dark:border-[rgba(253,253,253,0.12)] dark:bg-[rgba(28,28,28,0.7)]'
        }
      >
        <span className="flex h-12 w-12 items-center justify-center rounded-full bg-[rgba(45,41,35,0.08)] dark:bg-[rgba(253,253,253,0.1)]">
          <Lock size={20} className="text-[#2d2923] dark:text-[#fdfdfd]" />
        </span>

        <h1 className="font-plus-jakarta text-[22px] font-semibold leading-[30px] tracking-[-0.2px] text-[#2d2923] dark:text-[#fdfdfd]">
          Verification required
        </h1>

        <p className="max-w-[400px] font-plus-jakarta text-[14px] font-normal leading-[20px] text-[rgba(45,41,35,0.64)] dark:text-[rgba(253,253,253,0.64)]">
          {what ?? 'This page'} shows pricing, valuations and order detail. Finish verifying your
          institution and it opens up — we will bring you straight back here.
        </p>

        <div className="mt-2 flex flex-col gap-2 sm:flex-row">
          <button
            type="button"
            onClick={() => enterPhaseTwo(location.pathname + location.search)}
            className={
              'h-11 bg-[#2d2923] px-5 font-plus-jakarta text-[14px] font-medium text-white ' +
              'transition-opacity hover:opacity-90 dark:bg-[#fdfdfd] dark:text-[#2d2923]'
            }
          >
            Continue verification
          </button>
          <button
            type="button"
            onClick={() => navigate('/')}
            className={
              'h-11 border border-[rgba(45,41,35,0.24)] px-5 font-plus-jakarta text-[14px] font-medium ' +
              'text-[#2d2923] transition-colors hover:border-[#2d2923] ' +
              'dark:border-[rgba(253,253,253,0.24)] dark:text-[#fdfdfd] dark:hover:border-[#fdfdfd]'
            }
          >
            Back to dashboard
          </button>
        </div>
      </div>
    </div>
  )
}
